import {
  AccountHolderType,
  ExternalBankAccount,
  ExternalBankAccountAccountType,
  ExternalBankAccountUpdateVerificationMethod,
  PaymentMethodType,
  PayoutDestinationType,
  PreferredPayoutSpeed
} from "@wingspanhq/wingspan-api-sdk";
import { WSMutationConfig, WSQueryCache } from "@ws-react-query";
import { externalBankAccountsApi, payersApi, payoutsApi } from "@api";
import {
  queriesExternalBankAccounts,
  queriesPayers,
  queriesPayouts
} from "@query";
import { selectorOwnPayoutSettings } from "@selectors/selectorOwnPayoutSettings";
import { IdempotencyKey } from "@utils/idempotencyKey";
import { useWSMutation } from "../../../query/helpers";
import { useUserId } from "../../../query/hooks/helpers";
import { QUERY_USERS_CLIENT } from "../../../query/users/keys";
import { usersService } from "../../../services/users";
import { WSServiceError } from "../../../utils/serviceHelper";
import { useModalVerificationRequest } from "./ModalVerificationRequest";
import { FormData, ManualAccountSubType, ManualAccountType } from "./types";

export const useMutationSubmit = (
  params?: {
    setStandardPayoutDestination?: boolean;
    setInstantPayoutDestination?: boolean;
    requestVerification?: boolean;
    setDefaultPaymentMethod?: boolean;
    setFundingSource?: boolean;
    hideAccountHolderName?: boolean;
    type?: ManualAccountType;
    subType?: ManualAccountSubType;
  },
  config?: WSMutationConfig<ExternalBankAccount>
) => {
  const userId = useUserId();
  const modalVerificationRequest = useModalVerificationRequest();

  return useWSMutation<ExternalBankAccount, WSServiceError, FormData>(
    async values => {
      if (params?.requestVerification) {
        await modalVerificationRequest.open();
      }

      const idempotencyKey = new IdempotencyKey({
        accountNumber: values.accountNumber,
        routingNumber: values.accountRoutingNumber
      });

      let externalBankAccount =
        await externalBankAccountsApi.createExternalBankAccount({
          idempotencyKey: idempotencyKey.value,
          externalBankAccountCreate: {
            routingNumber: values.accountRoutingNumber,
            accountNumber: values.accountNumber,
            accountType:
              (params?.subType || values.type) === "savings"
                ? ExternalBankAccountAccountType.SAVINGS
                : ExternalBankAccountAccountType.CHECKING,
            accountHolderType:
              params?.type === "business"
                ? AccountHolderType.BUSINESS
                : AccountHolderType.INDIVIDUAL,
            // The payout paths hide this input on purpose, so there is no name
            // to send. accountHolderName is optional on v3, so omit the key
            // instead of sending an empty placeholder — the API then derives
            // the receiving-party name from the account's compliance entity.
            ...(params?.hideAccountHolderName
              ? {}
              : { accountHolderName: values.accountHolderName })
          }
        });

      if (params?.requestVerification) {
        externalBankAccount =
          await externalBankAccountsApi.updateExternalBankAccount({
            bankAccountId: externalBankAccount.id,
            externalBankAccountUpdate: {
              verificationMethod:
                ExternalBankAccountUpdateVerificationMethod.MICRO_DEPOSIT
            }
          });
      }

      if (
        params?.setStandardPayoutDestination ||
        params?.setInstantPayoutDestination
      ) {
        const payoutSettingsList =
          await queriesPayouts.listPayoutSettings.fetchQueryData();
        const payoutSettingsId =
          selectorOwnPayoutSettings(payoutSettingsList)?.id;

        if (payoutSettingsId && params?.setStandardPayoutDestination) {
          await payoutsApi.updatePayoutSettings({
            payoutSettingsId,
            payoutSettingsUpdate: {
              preferredPayoutSpeed: PreferredPayoutSpeed.STANDARD,
              standardDestination: {
                id: externalBankAccount.id,
                type: PayoutDestinationType.EXTERNAL_BANK_ACCOUNT
              }
            }
          });
        } else if (payoutSettingsId && params?.setInstantPayoutDestination) {
          await payoutsApi.updatePayoutSettings({
            payoutSettingsId,
            payoutSettingsUpdate: {
              preferredPayoutSpeed: PreferredPayoutSpeed.INSTANT,
              instantDestination: {
                id: externalBankAccount.id,
                type: PayoutDestinationType.EXTERNAL_BANK_ACCOUNT
              }
            }
          });
        }
      } else if (params?.setFundingSource) {
        const payerSettings = await payersApi.updateCurrentPayerSettings({
          currentPayerSettingsUpdate: {
            payrollSettings: {
              defaultFundingSource: {
                type: PaymentMethodType.EXTERNAL_BANK_ACCOUNT,
                id: externalBankAccount.id
              }
            }
          }
        });

        queriesPayers.getCurrentPayerSettings.setQueryData(
          undefined,
          payerSettings
        );
      }
      if (params?.setDefaultPaymentMethod) {
        const client = await usersService.client.update(userId, {
          clientId: userId,
          profile: {
            defaultPaymentMethod: {
              accountId: externalBankAccount.id,
              paymentMethodId: null
            }
          }
        });

        WSQueryCache.setQueryData(QUERY_USERS_CLIENT, client);
      }

      // Released only once every follow-up write has landed: a failure after the
      // account exists must replay onto the same account instead of creating a
      // second one (PLA-3760).
      idempotencyKey.destroy();

      return externalBankAccount;
    },
    {
      dependencies: [
        queriesExternalBankAccounts.listExternalBankAccounts.queryKeyBase,
        queriesPayers.getCurrentPayerSettings.queryKeyBase,
        queriesPayouts.listPayoutSettings.queryKeyBase,
        queriesPayouts.getPayoutSettings.queryKeyBase
      ],
      ...config
    }
  );
};
